import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Building2,
  Users,
  Calendar,
  RefreshCw,
  FileText,
  Filter,
  Download,
  Search
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  LabelList
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate } from '../utils/dateHelpers';
import './TableroGeneral.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#2196F3', '#F44336', '#4CAF50', '#FF9800', '#9C27B0'];

const TableroGeneral = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [gestiones, setGestiones] = useState([]);
  const [gestionesFiltradas, setGestionesFiltradas] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [exportandoPDF, setExportandoPDF] = useState(false);
  const dashboardRef = useRef(null);
  
  // Preseleccionar filtro de asesor para usuarios con rol 'analyst'
  const getInitialFiltros = () => {
    const baseFiltros = {
      institucion: '',
      asesor_cnc: '',
      fecha_desde: '',
      fecha_hasta: ''
    };
    
    // Si el usuario es analista, preseleccionar su nombre
    if (user?.role === 'analyst' && user?.full_name) {
      baseFiltros.asesor_cnc = user.full_name;
    }
    
    return baseFiltros;
  };
  
  // Estados de filtros
  const [filtros, setFiltros] = useState(getInitialFiltros());
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);

  // Actualizar filtros y aplicar automáticamente cuando el usuario sea analista
  useEffect(() => {
    if (user?.role === 'analyst' && user?.full_name) {
      setFiltros(prev => ({ ...prev, asesor_cnc: user.full_name }));
      setFiltrosAplicados(true);
    }
  }, [user]);

  useEffect(() => {
    fetchAllData();
  }, []);
  
  // Aplicar filtros cuando cambien las gestiones o los filtros
  useEffect(() => {
    if (filtrosAplicados) {
      aplicarFiltros();
    } else {
      setGestionesFiltradas(gestiones);
    }
  }, [gestiones, filtrosAplicados]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [estadisticasRes, gestionesRes, asesoresRes, institucionesRes] = await Promise.all([
        axios.get(`${API}/gestiones/estadisticas/general`, { headers }),
        axios.get(`${API}/gestiones?limit=1000`, { headers }),
        axios.get(`${API}/catalogos/asesores-cnc?activo=true`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setEstadisticas(estadisticasRes.data);
      const gestionesData = gestionesRes.data.gestiones || gestionesRes.data || [];
      setGestiones(gestionesData);
      setGestionesFiltradas(gestionesData);
      setAsesores(asesoresRes.data || []);
      // Mapear instituciones del nuevo formato
      const instData = (institucionesRes.data || []).map(i => ({
        id: i.id || i.nombre,
        value: i.nombre,
        is_active: true
      }));
      setInstituciones(instData);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = () => fetchAllData();
  
  // Aplicar filtros a las gestiones
  const aplicarFiltros = () => {
    let resultado = [...gestiones];
    
    if (filtros.institucion) {
      resultado = resultado.filter(g => g.institucion === filtros.institucion);
    }
    
    if (filtros.asesor_cnc) {
      resultado = resultado.filter(g => g.nombre_asesor_cnc === filtros.asesor_cnc);
    }
    
    if (filtros.fecha_desde) {
      resultado = resultado.filter(g => new Date(g.fecha_asignacion) >= new Date(filtros.fecha_desde));
    }
    
    if (filtros.fecha_hasta) {
      resultado = resultado.filter(g => new Date(g.fecha_asignacion) <= new Date(filtros.fecha_hasta));
    }
    
    setGestionesFiltradas(resultado);
  };
  
  const handleBuscar = () => {
    setFiltrosAplicados(true);
    aplicarFiltros();
  };
  
  const handleLimpiarFiltros = () => {
    // Si es analista, mantener su nombre en el filtro
    const nuevosFiltros = {
      institucion: '',
      asesor_cnc: user?.role === 'analyst' && user?.full_name ? user.full_name : '',
      fecha_desde: '',
      fecha_hasta: ''
    };
    setFiltros(nuevosFiltros);
    
    // Si es analista, mantener filtros aplicados ya que siempre filtra por su nombre
    if (user?.role === 'analyst') {
      setFiltrosAplicados(true);
    } else {
      setFiltrosAplicados(false);
      setGestionesFiltradas(gestiones);
    }
  };
  
  // Exportar a PDF
  const handleExportarPDF = async () => {
    if (!dashboardRef.current) return;
    
    setExportandoPDF(true);
    try {
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f5f7fa'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 297; // A4 landscape width in mm
      const pageHeight = 210; // A4 landscape height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Primera página
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Páginas adicionales si es necesario
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Header con fecha
      const fecha = new Date().toLocaleDateString('es-GT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      pdf.save(`Tablero_General_ALERTA360_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error al exportar PDF:', error);
    } finally {
      setExportandoPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="tablero-loading">
        <div className="loader"></div>
        <p>Cargando estadísticas del sistema...</p>
      </div>
    );
  }

  if (!estadisticas) {
    return (
      <div className="tablero-error">
        <AlertCircle size={48} />
        <p>No se pudieron cargar las estadísticas</p>
      </div>
    );
  }

  // Preparar datos para gráficos
  const dataPorModulo = Object.entries(estadisticas.por_tipo || {}).map(([key, value]) => ({
    name: key.replace('Monitoreo de Medios', 'MM')
           .replace('Monitoreo', 'MM')
           .replace('Banderas Rojas', 'AP')
           .replace('Adquisiciones públicas', 'AP')
           .replace('Requerimientos de Instancias de Probidad', 'CDP')
           .replace('Requerimientos IA', 'CDP')
           .replace('Contratación Personal', 'CDP')
           .replace('Gestión de Información', 'DE')
           .replace('Dirección Ejecutiva', 'DE')
           .replace('Seguimiento', 'CP')
           .replace('Casos previos', 'CP'),
    casos: value,
    fullName: key
  }));

  const dataPorEstado = Object.entries(estadisticas.por_estado || {}).map(([key, value]) => ({
    name: key.substring(0, 15) + (key.length > 15 ? '...' : ''),
    fullName: key,
    value: value
  }));

  const getColorForEstado = (estado) => {
    const colors = {
      'Registrado': '#9E9E9E',
      'En proceso de análisis': '#2196F3',
      'Enviado / Compartido': '#FF9800',
      'En Revisión': '#9C27B0',
      'En espera de otra gestión': '#795548',
      'Completada / Archivada': '#4CAF50'
    };
    return colors[estado] || '#757575';
  };

  // Preparar datos de tendencia mensual
  const getTendenciaMensual = () => {
    const mesesData = {};
    const nombresMeses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    
    gestionesFiltradas.forEach(g => {
      if (g.fecha_asignacion) {
        const fecha = new Date(g.fecha_asignacion);
        const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        mesesData[key] = (mesesData[key] || 0) + 1;
      }
    });

    return Object.entries(mesesData)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6) // Últimos 6 meses
      .map(([key, value]) => {
        const [year, month] = key.split('-');
        return {
          mes: `${nombresMeses[parseInt(month) - 1]} ${year.slice(2)}`,
          gestiones: value
        };
      });
  };

  // Datos de gestiones por institución (top 5)
  const getGestionesPorInstitucion = () => {
    const institucionesData = {};
    gestionesFiltradas.forEach(g => {
      if (g.institucion) {
        institucionesData[g.institucion] = (institucionesData[g.institucion] || 0) + 1;
      }
    });

    return Object.entries(institucionesData)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([nombre, cantidad]) => ({
        name: nombre.length > 20 ? nombre.substring(0, 20) + '...' : nombre,
        fullName: nombre,
        casos: cantidad
      }));
  };

  // Datos de gestiones por asesor
  const getGestionesPorAsesor = () => {
    const asesoresData = {};
    gestionesFiltradas.forEach(g => {
      if (g.nombre_asesor_cnc) {
        asesoresData[g.nombre_asesor_cnc] = (asesoresData[g.nombre_asesor_cnc] || 0) + 1;
      }
    });

    return Object.entries(asesoresData)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([nombre, cantidad]) => ({
        name: nombre.length > 15 ? nombre.substring(0, 15) + '...' : nombre,
        fullName: nombre,
        casos: cantidad
      }));
  };

  // Calcular métricas dinámicas basadas en filtros
  const calcularEstadisticasFiltradas = () => {
    const total = gestionesFiltradas.length;
    const porEstado = {};
    const porTipo = {};
    let vencidasCNCCount = 0;
    let vencidasProbidadCount = 0;
    let gestionesConVencimiento = 0; // Gestiones únicas que tienen algún vencimiento
    let pendientesDerivacionCount = 0;
    let completadasEnTiempo = 0;
    let completadasFueraPlazo = 0;
    let completadasFueraPlazo_CNC = 0;
    let completadasFueraPlazo_IAP = 0;
    let derivadasProbidadCount = 0;
    let improcedentesCount = 0;
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    gestionesFiltradas.forEach(g => {
      // Por estado
      porEstado[g.estado] = (porEstado[g.estado] || 0) + 1;
      // Por tipo
      porTipo[g.tipo_gestion] = (porTipo[g.tipo_gestion] || 0) + 1;
      
      const yaDerivadaProbidad = g.asesor_probidad_id || g.asesor_probidad_nombre;
      
      // Vencidas CNC: solo las que NO han sido derivadas a probidad y no están completadas
      // Si ya fue derivada a Probidad, se considera "atendida en tiempo" para CNC
      // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
      const fechaVencCNCParsed = g.fecha_vencimiento ? parseLocalDate(g.fecha_vencimiento) : null;
      const vencidaCNC = fechaVencCNCParsed && 
                         hoy > fechaVencCNCParsed && 
                         g.estado !== 'Completada / Archivada' &&
                         !yaDerivadaProbidad;
      
      // Vencidas Probidad: 
      // SOLO si tiene fecha_vencimiento_probidad y está vencida
      // NO usar fallback de fecha_vencimiento (eso es vencimiento CNC)
      // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
      const fechaVencProbParsed = g.fecha_vencimiento_probidad ? parseLocalDate(g.fecha_vencimiento_probidad) : null;
      const vencidaProbidad = fechaVencProbParsed && hoy > fechaVencProbParsed && g.estado !== 'Completada / Archivada';
      
      if (vencidaCNC) vencidasCNCCount++;
      if (vencidaProbidad) vencidasProbidadCount++;
      
      // Contar gestiones únicas vencidas (no sumar doble si ambas están vencidas)
      if (vencidaCNC || vencidaProbidad) {
        gestionesConVencimiento++;
      }
      
      // Pendientes de derivación: tiene asesor CNC pero NO tiene asesor probidad y no está completada
      if (g.nombre_asesor_cnc && !yaDerivadaProbidad && g.estado !== 'Completada / Archivada') {
        pendientesDerivacionCount++;
      }
      
      // Derivadas a Probidad
      if (yaDerivadaProbidad) {
        derivadasProbidadCount++;
      }
      
      // Improcedentes - solo contar si el estado actual sigue siendo Completada/Archivada
      // y tiene la marca de improcedente (resultado_final o es_procedente)
      if (g.estado === 'Completada / Archivada' && (g.resultado_final === 'Improcedente' || g.es_procedente === false)) {
        improcedentesCount++;
      }
      
      // Clasificar completadas: en tiempo vs fuera de plazo
      if (g.estado === 'Completada / Archivada') {
        const fechaCompletado = g.updatedAt ? parseLocalDate(g.updatedAt) : new Date();
        const fechaVencCNC = g.fecha_vencimiento ? parseLocalDate(g.fecha_vencimiento) : null;
        const fechaVencIP = g.fecha_vencimiento_probidad ? parseLocalDate(g.fecha_vencimiento_probidad) : null;
        
        // Determinar si CNC completó fuera de plazo
        // Nota: Si fue derivada a probidad, se considera "en tiempo" para CNC si la derivación fue antes del vencimiento
        let fueraPlazo_CNC = false;
        if (fechaVencCNC && !yaDerivadaProbidad) {
          // Si no fue derivada, comparar completado vs vencimiento CNC
          fueraPlazo_CNC = fechaCompletado > fechaVencCNC;
        } else if (fechaVencCNC && yaDerivadaProbidad && g.fecha_asignacion_probidad) {
          // Si fue derivada, comparar fecha de derivación vs vencimiento CNC
          const fechaDerivacion = parseLocalDate(g.fecha_asignacion_probidad);
          fueraPlazo_CNC = fechaDerivacion && fechaDerivacion > fechaVencCNC;
        }
        
        // Determinar si IAP/Probidad completó fuera de plazo
        let fueraPlazo_IAP = false;
        if (fechaVencIP) {
          fueraPlazo_IAP = fechaCompletado > fechaVencIP;
        }
        
        if (fueraPlazo_CNC) completadasFueraPlazo_CNC++;
        if (fueraPlazo_IAP) completadasFueraPlazo_IAP++;
        
        // General: fuera de plazo si alguna de las dos lo está
        const fueraDelPlazoGeneral = fueraPlazo_CNC || fueraPlazo_IAP;
        
        if (fueraDelPlazoGeneral) {
          completadasFueraPlazo++;
        } else {
          completadasEnTiempo++;
        }
      }
    });
    
    return { 
      total, 
      porEstado, 
      porTipo, 
      vencidasCNC: vencidasCNCCount, 
      vencidasProbidad: vencidasProbidadCount,
      gestionesVencidasUnicas: gestionesConVencimiento,
      pendientes_derivacion: pendientesDerivacionCount,
      completadas_en_tiempo: completadasEnTiempo,
      completadas_fuera_plazo: completadasFueraPlazo,
      completadas_fuera_plazo_cnc: completadasFueraPlazo_CNC,
      completadas_fuera_plazo_iap: completadasFueraPlazo_IAP,
      derivadas_probidad: derivadasProbidadCount,
      improcedentes: improcedentesCount
    };
  };
  
  // SIEMPRE calcular estadísticas locales desde gestionesFiltradas para tener métricas correctas
  const estadisticasFiltradas = calcularEstadisticasFiltradas();
  
  // Calcular métricas adicionales - usando gestiones únicas vencidas para el cálculo correcto
  const vencidasCNC = estadisticasFiltradas.vencidas_cnc || estadisticasFiltradas.vencidasCNC || 0;
  const vencidasProbidad = estadisticasFiltradas.vencidas_probidad || estadisticasFiltradas.vencidasProbidad || 0;
  
  // Para el % de cumplimiento: usar gestiones únicas vencidas del cálculo local
  const gestionesVencidas = estadisticasFiltradas.gestionesVencidasUnicas || 0;
  const gestionesATiempo = Math.max(0, (estadisticasFiltradas.total || 0) - gestionesVencidas);
  const porcentajeCumplimiento = (estadisticasFiltradas.total || 0) > 0 
    ? ((gestionesATiempo / estadisticasFiltradas.total) * 100).toFixed(1) 
    : 0;
  
  // Calcular promedio de calificaciones desde las gestiones completadas
  const gestionesConCalificacion = gestionesFiltradas.filter(g => g.calificacion_instancia && g.calificacion_instancia.puntuacion);
  const promedioCalificaciones = gestionesConCalificacion.length > 0
    ? (gestionesConCalificacion.reduce((acc, g) => acc + (g.calificacion_instancia.puntuacion || 0), 0) / gestionesConCalificacion.length).toFixed(1)
    : 0;
  const totalCalificaciones = gestionesConCalificacion.length;

  const dataTendencia = getTendenciaMensual();
  const dataInstituciones = getGestionesPorInstitucion();
  const dataAsesores = getGestionesPorAsesor();
  
  // Datos para gráficos basados en filtros
  const dataPorModuloFiltrado = filtrosAplicados 
    ? Object.entries(estadisticasFiltradas.porTipo || {}).map(([key, value]) => ({
        name: key.replace('Monitoreo de Medios', 'MM')
               .replace('Monitoreo', 'MM')
               .replace('Banderas Rojas', 'AP')
               .replace('Adquisiciones públicas', 'AP')
               .replace('Requerimientos de Instancias de Probidad', 'CDP')
               .replace('Requerimientos IA', 'CDP')
               .replace('Contratación Personal', 'CDP')
               .replace('Gestión de Información', 'DE')
               .replace('Dirección Ejecutiva', 'DE')
               .replace('Seguimiento', 'CP')
               .replace('Casos previos', 'CP'),
        casos: value,
        fullName: key
      }))
    : dataPorModulo;
    
  const dataPorEstadoFiltrado = filtrosAplicados
    ? Object.entries(estadisticasFiltradas.porEstado || {}).map(([key, value]) => ({
        name: key.substring(0, 15) + (key.length > 15 ? '...' : ''),
        fullName: key,
        value: value
      }))
    : dataPorEstado;

  return (
    <div className="tablero-general-container" ref={dashboardRef}>
      {/* Header */}
      <div className="tablero-header">
        <div>
          <h1>
            <BarChart3 size={32} />
            Tablero General - ALERTA 360
          </h1>
          <p>
            Vista consolidada de todas las gestiones del sistema
            {filtrosAplicados && (
              <span style={{ marginLeft: '0.5rem', color: 'var(--color-primary)', fontWeight: 600 }}>
                (Filtrado: {gestionesFiltradas.length} gestiones)
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-secondary"
            onClick={fetchEstadisticas}
            title="Actualizar datos"
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleExportarPDF}
            disabled={exportandoPDF}
            title="Exportar a PDF"
          >
            <Download size={16} />
            {exportandoPDF ? 'Exportando...' : 'Exportar PDF'}
          </button>
        </div>
      </div>
      
      {/* Panel de Filtros */}
      <div className="filtros-panel" style={{
        background: 'white',
        padding: '1rem 1.5rem',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        alignItems: 'flex-end'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>
            <Building2 size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Institución
          </label>
          <select
            value={filtros.institucion}
            onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
          >
            <option value="">Todas las instituciones</option>
            {instituciones.map(inst => (
              <option key={inst.id} value={inst.value}>{inst.value}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: '1', minWidth: '180px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>
            <Users size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Asesor CNC
            {user?.role === 'analyst' && (
              <span style={{ fontSize: '0.7rem', color: '#888', marginLeft: '0.25rem' }}>(fijo)</span>
            )}
          </label>
          <select
            value={filtros.asesor_cnc}
            onChange={(e) => setFiltros({...filtros, asesor_cnc: e.target.value})}
            disabled={user?.role === 'analyst'}
            style={{ 
              width: '100%', 
              padding: '0.5rem', 
              borderRadius: '6px', 
              border: '1px solid var(--color-border)',
              ...(user?.role === 'analyst' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {})
            }}
          >
            <option value="">Todos los asesores</option>
            {asesores.map(asesor => (
              <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: '0.8', minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>
            <Calendar size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Fecha Desde
          </label>
          <input
            type="date"
            value={filtros.fecha_desde}
            onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
          />
        </div>
        
        <div style={{ flex: '0.8', minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)' }}>
            <Calendar size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Fecha Hasta
          </label>
          <input
            type="date"
            value={filtros.fecha_hasta}
            onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--color-border)' }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            className="btn btn-primary"
            onClick={handleBuscar}
            style={{ padding: '0.5rem 1rem' }}
          >
            <Search size={16} />
            Filtrar
          </button>
          {filtrosAplicados && (
            <button 
              className="btn btn-secondary"
              onClick={handleLimpiarFiltros}
              style={{ padding: '0.5rem 1rem' }}
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* KPIs Cards - Fila 1 */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <div className="kpi-icon">
            <TrendingUp size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{estadisticasFiltradas.total || 0}</div>
            <div className="kpi-label">Total de Gestiones</div>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-icon">
            <Clock size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{(estadisticasFiltradas.por_estado || estadisticasFiltradas.porEstado)?.['Registrado'] || 0}</div>
            <div className="kpi-label">Registradas</div>
          </div>
        </div>

        <div className="kpi-card kpi-info">
          <div className="kpi-icon">
            <AlertCircle size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{(estadisticasFiltradas.por_estado || estadisticasFiltradas.porEstado)?.['En proceso de análisis'] || 0}</div>
            <div className="kpi-label">En Proceso Análisis</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #FF9800' }}>
          <div className="kpi-icon">
            <Users size={32} style={{ color: '#FF9800' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#FF9800' }}>{estadisticasFiltradas.asignadas_probidad || gestionesFiltradas.filter(g => g.asesor_probidad_nombre).length}</div>
            <div className="kpi-label">Asignadas</div>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-icon">
            <CheckCircle size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{(estadisticasFiltradas.por_estado || estadisticasFiltradas.porEstado)?.['Completada / Archivada'] || 0}</div>
            <div className="kpi-label">Completadas</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #4CAF50' }}>
          <div className="kpi-icon">
            <FileText size={32} style={{ color: '#4CAF50' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#4CAF50' }}>{porcentajeCumplimiento}%</div>
            <div className="kpi-label">Cumplimiento</div>
          </div>
        </div>
      </div>

      {/* KPIs Cards - Fila 2: Vencimientos y Calificaciones */}
      <div className="kpi-grid" style={{ marginTop: '1rem' }}>
        <div className="kpi-card kpi-danger">
          <div className="kpi-icon">
            <AlertCircle size={32} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value">{vencidasCNC}</div>
            <div className="kpi-label">Vencidas CNC</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #FF5722' }}>
          <div className="kpi-icon">
            <AlertCircle size={32} style={{ color: '#FF5722' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#FF5722' }}>{vencidasProbidad}</div>
            <div className="kpi-label">Vencidas Probidad</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #9C27B0' }}>
          <div className="kpi-icon">
            <Building2 size={32} style={{ color: '#9C27B0' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#9C27B0' }}>{estadisticasFiltradas.pendientes_derivacion || 0}</div>
            <div className="kpi-label">Pendientes Derivación</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #FFB800' }}>
          <div className="kpi-icon">
            <span style={{ fontSize: '2rem', color: '#FFB800' }}>⭐</span>
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#FFB800' }}>{promedioCalificaciones}</div>
            <div className="kpi-label">Promedio Calificaciones</div>
            <div style={{ fontSize: '0.7rem', color: '#666' }}>({totalCalificaciones} evaluaciones)</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #00C853' }}>
          <div className="kpi-icon">
            <CheckCircle size={32} style={{ color: '#00C853' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#00C853' }}>{estadisticasFiltradas.completadas_en_tiempo || 0}</div>
            <div className="kpi-label">Completadas en Tiempo</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #FF1744' }}>
          <div className="kpi-icon">
            <AlertCircle size={32} style={{ color: '#FF1744' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#FF1744' }}>{estadisticasFiltradas.completadas_fuera_plazo || 0}</div>
            <div className="kpi-label">Completadas Fuera Plazo</div>
          </div>
        </div>
      </div>

      {/* KPIs Cards - Fila 3: Adicionales */}
      <div className="kpi-grid" style={{ marginTop: '1rem' }}>
        <div className="kpi-card" style={{ borderTop: '4px solid #607D8B' }}>
          <div className="kpi-icon">
            <Clock size={32} style={{ color: '#607D8B' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#607D8B' }}>{(estadisticasFiltradas.por_estado || estadisticasFiltradas.porEstado)?.['En espera de otra gestión'] || 0}</div>
            <div className="kpi-label">En Espera</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #00BCD4' }}>
          <div className="kpi-icon">
            <FileText size={32} style={{ color: '#00BCD4' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#00BCD4' }}>{(estadisticasFiltradas.por_estado || estadisticasFiltradas.porEstado)?.['Enviado / Compartido'] || 0}</div>
            <div className="kpi-label">Enviado/Compartido</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #3F51B5' }}>
          <div className="kpi-icon">
            <Users size={32} style={{ color: '#3F51B5' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#3F51B5' }}>{estadisticasFiltradas.derivadas_probidad || gestionesFiltradas.filter(g => g.asesor_probidad_id || g.asesor_probidad_nombre).length}</div>
            <div className="kpi-label">Derivadas Probidad</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #795548' }}>
          <div className="kpi-icon">
            <AlertCircle size={32} style={{ color: '#795548' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#795548' }}>{estadisticasFiltradas.improcedentes || gestionesFiltradas.filter(g => g.estado === 'Completada / Archivada' && (g.resultado_final === 'Improcedente' || g.es_procedente === false)).length}</div>
            <div className="kpi-label">Improcedentes</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #E91E63' }}>
          <div className="kpi-icon">
            <Clock size={32} style={{ color: '#E91E63' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#E91E63' }}>{estadisticasFiltradas.completadas_fuera_plazo_cnc || 0}</div>
            <div className="kpi-label">Compl. Fuera Plazo (CNC)</div>
          </div>
        </div>

        <div className="kpi-card" style={{ borderTop: '4px solid #673AB7' }}>
          <div className="kpi-icon">
            <Clock size={32} style={{ color: '#673AB7' }} />
          </div>
          <div className="kpi-content">
            <div className="kpi-value" style={{ color: '#673AB7' }}>{estadisticasFiltradas.completadas_fuera_plazo_iap || 0}</div>
            <div className="kpi-label">Compl. Fuera Plazo (IAP)</div>
          </div>
        </div>
      </div>

      {/* Gráficos - Fila 1 */}
      <div className="charts-grid">
        {/* Gráfico por Módulo */}
        <div className="chart-card">
          <h2>Casos por Módulo</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataPorModuloFiltrado}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="custom-tooltip">
                        <p className="label">{payload[0].payload.fullName}</p>
                        <p className="value">Casos: {payload[0].value}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Bar dataKey="casos" fill="#2196F3" name="Número de Casos" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="casos" position="top" style={{ fontSize: '12px', fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico por Estado */}
        <div className="chart-card">
          <h2>Distribución por Estado</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dataPorEstadoFiltrado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                outerRadius={100}
                innerRadius={40}
                fill="#8884d8"
                dataKey="value"
              >
                {dataPorEstadoFiltrado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColorForEstado(entry.fullName)} />
                ))}
              </Pie>
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="custom-tooltip">
                        <p className="label">{payload[0].payload.fullName}</p>
                        <p className="value">Casos: {payload[0].value}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráficos - Fila 2: Tendencias y Top */}
      <div className="charts-grid">
        {/* Tendencia Mensual */}
        <div className="chart-card">
          <h2>
            <Calendar size={20} style={{ marginRight: '0.5rem' }} />
            Tendencia de Gestiones (Últimos 6 meses)
          </h2>
          {dataTendencia.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dataTendencia}>
                <defs>
                  <linearGradient id="colorGestiones" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2196F3" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#2196F3" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="gestiones" 
                  stroke="#2196F3" 
                  fillOpacity={1} 
                  fill="url(#colorGestiones)"
                  name="Gestiones"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#757575' }}>
              No hay datos suficientes para mostrar la tendencia
            </div>
          )}
        </div>

        {/* Top Instituciones */}
        <div className="chart-card">
          <h2>
            <Building2 size={20} style={{ marginRight: '0.5rem' }} />
            Top 5 Instituciones con más Gestiones
          </h2>
          {dataInstituciones.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dataInstituciones} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="custom-tooltip">
                          <p className="label">{payload[0].payload.fullName}</p>
                          <p className="value">Casos: {payload[0].value}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="casos" fill="#4CAF50" name="Casos" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="casos" position="right" style={{ fontSize: '11px', fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#757575' }}>
              No hay datos de instituciones
            </div>
          )}
        </div>
      </div>

      {/* Gráficos - Fila 3: Asesores y Cumplimiento */}
      <div className="charts-grid">
        {/* Top Asesores */}
        <div className="chart-card">
          <h2>
            <Users size={20} style={{ marginRight: '0.5rem' }} />
            Gestiones por Asesor CNC
          </h2>
          {dataAsesores.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dataAsesores}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="custom-tooltip">
                          <p className="label">{payload[0].payload.fullName}</p>
                          <p className="value">Casos asignados: {payload[0].value}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="casos" fill="#9C27B0" name="Casos Asignados" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="casos" position="top" style={{ fontSize: '12px', fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#757575' }}>
              No hay datos de asesores
            </div>
          )}
        </div>

        {/* Cumplimiento (A tiempo vs Vencidas) */}
        <div className="chart-card">
          <h2>
            <CheckCircle size={20} style={{ marginRight: '0.5rem' }} />
            Cumplimiento de Plazos
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'A Tiempo', value: gestionesATiempo, color: '#4CAF50' },
                  { name: 'Vencidas', value: gestionesVencidas, color: '#F44336' }
                ]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
              >
                <Cell fill="#4CAF50" />
                <Cell fill="#F44336" />
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: '-1rem', fontSize: '0.875rem', color: '#757575' }}>
            Tasa de cumplimiento: <strong style={{ color: porcentajeCumplimiento >= 70 ? '#4CAF50' : '#F44336' }}>{porcentajeCumplimiento}%</strong>
          </div>
        </div>
      </div>

      {/* Resumen por Módulo */}
      <div className="modulos-summary">
        <h2>Resumen por Módulo</h2>
        <div className="modulos-grid">
          {Object.entries(estadisticas.por_tipo || {}).map(([modulo, cantidad]) => {
            const colores = {
              'Monitoreo de Medios': '#2196F3',
              'Banderas Rojas': '#F44336',
              'Gestión de Información': '#4CAF50',
              'Requerimientos IAP': '#FF9800',
              'Seguimiento': '#9C27B0'
            };
            
            const siglas = {
              'Monitoreo de Medios': 'MM',
              'Banderas Rojas': 'BR',
              'Gestión de Información': 'RIP',
              'Requerimientos IAP': 'GI',
              'Seguimiento': 'SEG'
            };

            return (
              <div 
                key={modulo} 
                className="modulo-summary-card"
                style={{ borderLeftColor: colores[modulo] }}
              >
                <div className="modulo-summary-header">
                  <span 
                    className="modulo-sigla"
                    style={{ backgroundColor: colores[modulo] }}
                  >
                    {siglas[modulo]}
                  </span>
                  <h3>{modulo}</h3>
                </div>
                <div className="modulo-summary-value">{cantidad}</div>
                <div className="modulo-summary-label">casos registrados</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info adicional con datos reales */}
      <div className="info-cards-grid">
        <div className="info-card">
          <Building2 size={24} />
          <h3>Instituciones Activas</h3>
          <div className="info-value">{instituciones.length}</div>
          <p>Entidades del Organismo Ejecutivo registradas</p>
        </div>
        <div className="info-card">
          <Users size={24} />
          <h3>Asesores CNC Activos</h3>
          <div className="info-value">{asesores.length}</div>
          <p>Profesionales gestionando casos en el sistema</p>
        </div>
        <div className="info-card">
          <Calendar size={24} />
          <h3>Módulos Disponibles</h3>
          <div className="info-value">{Object.keys(estadisticas.por_tipo || {}).length}</div>
          <p>Tipos de gestiones habilitados</p>
        </div>
        <div className="info-card">
          <RefreshCw size={24} />
          <h3>Última Actualización</h3>
          <div className="info-value" style={{ fontSize: '1rem' }}>{new Date().toLocaleString('es-GT')}</div>
          <button 
            onClick={fetchEstadisticas} 
            className="btn btn-secondary btn-sm"
            style={{ marginTop: '0.5rem' }}
          >
            <RefreshCw size={14} />
            Actualizar Datos
          </button>
        </div>
      </div>
    </div>
  );
};

export default TableroGeneral;
